import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useCurrentSite, resolveLastSite } from '@/contexts/SiteContext';
import { useLocalFlag } from '@/hooks/useLocalFlag';
import { Tooltip } from '@/components/shared/Tooltip';
import { SiteSwitcher } from '@/components/layout/SiteSwitcher';
import { type NavItem, SITE_NAV } from '@/components/layout/navItems';
import { IconChevronLeft, IconChevronRight } from '@/lib/icons';
import type { Site } from '@/types';

// SiteWorkspaceNav is the wider column to the right of the GlobalRail. It hosts
// the persistent SiteSwitcher and the site-scoped nav items (Hub, Brainstorm,
// Pipeline, ...). It "sticks" to a site: the items target currentSite, or the
// last-active site when the operator is on a global route, so they never
// silently disappear. With zero sites the items render disabled (visible but
// non-interactive) beneath the switcher's "Select a site" empty state.
//
// Collapse is owned here (the rail is always slim): the existing
// `sidebar_collapsed` localStorage flag + the 1024px auto-collapse are reused
// unchanged.

function useViewportLg(): boolean {
  const [isLg, setIsLg] = useState<boolean>(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 1024,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsLg(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isLg;
}

function siteHref(slug: string, sub: string): string {
  return sub ? `/sites/${slug}/${sub}` : `/sites/${slug}`;
}

export function SiteWorkspaceNav() {
  const { currentSite, allSites } = useCurrentSite();
  const [override, setOverride] = useLocalFlag('sidebar_collapsed', false);
  const isLg = useViewportLg();
  const collapsed = override ? true : !isLg;

  // The site the column points at: the active site, or the sticky fallback.
  // resolveLastSite returns null only when no site exists at all.
  const target: Site | null = currentSite ?? resolveLastSite(allSites);

  return (
    <aside
      aria-label="Site"
      className={`${
        collapsed ? 'w-14' : 'w-48'
      } shrink-0 border-r border-dash-border bg-dash-surface/60 h-full flex flex-col transition-[width] duration-150`}
    >
      <div className={`${collapsed ? 'px-2' : 'px-3'} py-3 border-b border-dash-border`}>
        <SiteSwitcher collapsed={collapsed} />
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {SITE_NAV.map((item) =>
          target ? (
            <SiteRow
              key={item.to || 'hub'}
              item={item}
              href={siteHref(target.slug, item.to)}
              collapsed={collapsed}
            />
          ) : (
            <DisabledRow key={item.to || 'hub'} item={item} collapsed={collapsed} />
          ),
        )}
      </nav>

      <button
        type="button"
        onClick={() => setOverride(!override)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="mt-auto mx-2 mb-3 p-2 inline-flex items-center justify-center rounded border border-dash-border bg-dash-bg text-dash-secondary hover:text-dash-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
      >
        {collapsed ? <IconChevronRight size={14} /> : <IconChevronLeft size={14} />}
      </button>
    </aside>
  );
}

function SiteRow({
  item,
  href,
  collapsed,
}: {
  item: NavItem;
  href: string;
  collapsed: boolean;
}) {
  const Icon = item.icon;
  const link = (
    <NavLink
      to={href}
      end={item.end}
      className={({ isActive }) =>
        `relative flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors border-l-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-1 focus-visible:ring-offset-dash-bg ${
          isActive
            ? 'bg-accent/10 text-accent'
            : 'text-dash-secondary hover:bg-dash-surface hover:text-dash-primary border-transparent'
        } ${collapsed ? 'justify-center px-2' : ''}`
      }
      style={({ isActive }) =>
        isActive
          ? { borderLeftColor: 'var(--site-accent, var(--color-accent, #22d3ee))' }
          : undefined
      }
    >
      <Icon size={16} className="shrink-0" />
      <span className={collapsed ? 'sr-only' : 'flex-1'}>{item.label}</span>
    </NavLink>
  );
  return collapsed ? (
    <Tooltip placement="right" content={item.label}>
      {link}
    </Tooltip>
  ) : (
    link
  );
}

// DisabledRow renders a site-scoped item when there is no site to target
// (fresh account, zero sites). It is visible but inert: not a link, not
// focusable, and announced as disabled. This is the agreed "disabled, not
// hidden" empty state — the items never silently vanish.
function DisabledRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  const row = (
    <div
      aria-disabled="true"
      className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-sm border-l-2 border-transparent text-dash-dim cursor-not-allowed select-none ${
        collapsed ? 'justify-center px-2' : ''
      }`}
    >
      <Icon size={16} className="shrink-0" />
      <span className={collapsed ? 'sr-only' : 'flex-1'}>{item.label}</span>
    </div>
  );
  return collapsed ? (
    <Tooltip placement="right" content={`${item.label} (select a site first)`}>
      {row}
    </Tooltip>
  ) : (
    row
  );
}
